/**
 * `#uc2Similar` — "Most alike to {name}" (client §3.2, re-presented after the client round
 * of 2 October 2026): the three most alike watersheds, one `Uc2PairCard` each, read the
 * same way as the contrast pairs above them.
 *
 * The content is unchanged — the same three candidates, the same similarity, the same gap
 * and the same `Learning opportunity` flag, all precomputed on the selected watershed's
 * own detail payload (backend §3.3), so changing the practice group still costs no
 * request. What changed is that the reader can now see **both** adoption figures rather
 * than only the difference between them, and that the two halves of this tab are built
 * out of the same card instead of two dense rows that happened to look alike.
 *
 * Each card keeps its **Compare** button, which opens `#uc2Compare` with the same three
 * charts for both watersheds.
 */
import { useTranslation } from 'react-i18next';

import type { IntelSimilarRow } from '../../lib/api';
import { Uc2Notes } from './Uc2Notes';
import { Uc2PairCard } from './Uc2PairCard';
import { similarLookOut } from './uc2Captions';

export interface SimilarStripProps {
  /** The clicked watershed: the reference every card is read against. */
  reference: { id: string; name: string; category?: string | null; adoption: number | null };
  rows: IntelSimilarRow[];
  /**
   * One candidate's own class, from the units rows the pane holds — `IntelSimilarRow`
   * carries none, and without it every candidate drew neutral while the contrast cards
   * two blocks down drew the same watershed green (client round 2 Oct 2026).
   */
  categoryOf: (unitId: string) => string | null;
  /** That group's label, and the same word lower-cased for mid-sentence use. */
  practiceLabel: string;
  practiceLower: string;
  /** `summary.programme_means[group]` — the tick every bar is read against. */
  programmeMean?: number | null;
  /** `#uc2Compare` — which candidate is open beside the reference, if any. */
  compareWith: string | null;
  onCompare: (unitId: string) => void;
}

export function SimilarStrip({
  reference,
  rows,
  categoryOf,
  practiceLabel,
  practiceLower,
  programmeMean,
  compareWith,
  onCompare,
}: SimilarStripProps) {
  const { t } = useTranslation();

  /**
   * The three cards, with the similarity each one prints read once.
   *
   * A row from an older run carries no `similarity` (it had `distance` only): nothing
   * alike is printed rather than a `NaN %` the reader would believe.
   *
   * The plain notes under the block read the same list, so the sentence about a candidate
   * and the card above it can never quote two different percentages.
   */
  const cards = rows.slice(0, 3).map((row) => ({
    row,
    pct: Number.isFinite(row.similarity)
      ? Math.round(Math.min(1, Math.max(0, row.similarity)) * 100)
      : 0,
  }));

  return (
    <div id="uc2Similar" className="result-box">
      <h5>{t('intel.uc2.similarToTitle', { name: reference.name })}</h5>
      <div className="result-pad">
        {rows.length ? (
          <ul className="uc2-similar">
            {cards.map(({ row, pct }) => {
              return (
                <Uc2PairCard
                  key={row.unit_id}
                  testId={`uc2-similar-${row.unit_id}`}
                  // `gap` is reference − candidate, which is exactly the card's own `a − b`
                  // with the reference as `a`: the clicked watershed leads every card, so
                  // three cards in a row read as three readings of one place.
                  a={{
                    id: reference.id,
                    name: reference.name,
                    ...(reference.category ? { category: reference.category } : {}),
                    value: reference.adoption,
                  }}
                  b={{
                    id: row.unit_id,
                    name: row.name,
                    category: categoryOf(row.unit_id),
                    value: row.adoption,
                  }}
                  similarityPct={pct}
                  practiceLabel={practiceLabel}
                  practiceLower={practiceLower}
                  gap={typeof row.gap === 'number' ? row.gap : null}
                  learning={row.learning_opportunity}
                  programmeMean={programmeMean ?? null}
                  action={
                    <button
                      type="button"
                      className="btn ghost sm"
                      id={`uc2Compare-${row.unit_id}`}
                      aria-pressed={compareWith === row.unit_id}
                      onClick={() => onCompare(row.unit_id)}
                    >
                      {t('intel.uc2.compareBtn')}
                    </button>
                  }
                />
              );
            })}
          </ul>
        ) : (
          <p className="sub">{t('intel.uc2.similarEmpty')}</p>
        )}
        {cards.length ? (
          <Uc2Notes
            testId="uc2-similar-note"
            rows={[
              {
                label: t('intel.uc2.note.happening'),
                lines: [
                  t('intel.uc2.note.similarHappening', {
                    name: reference.name,
                    practice: practiceLower,
                  }),
                ],
              },
              {
                /* One line per candidate card above, in the same order, so the reader can
                   run a finger down the block and meet them in the order they were drawn. */
                label: t('intel.uc2.note.lookOut'),
                lines: cards.map(({ row, pct }) => {
                  const note = similarLookOut(
                    { name: row.name, category: categoryOf(row.unit_id) },
                    reference.name,
                    practiceLower,
                    pct,
                    typeof row.gap === 'number' ? row.gap : null,
                  );
                  return t(note.key, {
                    ...note.options,
                    pattern: t(note.patternKey),
                    ...(note.directionKey ? { direction: t(note.directionKey) } : {}),
                  });
                }),
              },
            ]}
          />
        ) : null}
      </div>
    </div>
  );
}

export default SimilarStrip;
